import { Injectable, signal, Signal, WritableSignal, TemplateRef } from '@angular/core';
import { Subject, Observable } from 'rxjs';

export interface DismissPayload {
  data?: any;
  role?: string;
}

export interface ControllableModalState {
  header?: string;
  message?: string;
  showHeader?: boolean;
  showCloseButton?: boolean;
  headerWithShadow?: boolean;
  centerHeaderTitle?: boolean;
  centerInnerContent?: boolean;
  showFooter?: boolean;
  showFooterButton?: boolean;
  footerButtonText?: string;
  footerButtonColor?: string;
  footerButtonDisabled?: boolean;
  footerButtonExpandMode?: "block" | "full" | null;
  footerAction?: (() => void | Promise<void>) | null;
  headerTemplate?: TemplateRef<any> | null;
  footerTemplate?: TemplateRef<any> | null;
}

@Injectable()
export class ModalStateService {

  private stateSignal: WritableSignal<ControllableModalState> = signal<ControllableModalState>({});
  public readonly state: Signal<ControllableModalState> = this.stateSignal.asReadonly();

  private closeSubject = new Subject<void>();
  public close$ = this.closeSubject.asObservable();

  private dismissSubject = new Subject<DismissPayload>();
  public dismiss$: Observable<DismissPayload> = this.dismissSubject.asObservable();

  setConfig(config: Partial<ControllableModalState>): void {
    this.stateSignal.update(currentState => ({ ...currentState, ...config }));
  }

  setHeader(header: string): void {
    this.stateSignal.update(state => ({ ...state, header }));
  }

  setShowHeader(show: boolean): void {
    this.stateSignal.update(state => ({ ...state, showHeader: show }));
  }

  setShowCloseButton(show: boolean): void {
    this.stateSignal.update(state => ({ ...state, showCloseButton: show }));
  }

  setFooterButtonDisabled(disabled: boolean): void {
    this.stateSignal.update(state => ({ ...state, footerButtonDisabled: disabled }));
  }

  setFooterButtonText(text: string): void {
    this.stateSignal.update(state => ({ ...state, footerButtonText: text }));
  }

  setFooterButtonColor(color: string): void {
    this.stateSignal.update(state => ({ ...state, footerButtonColor: color }));
  }

  setFooterAction(action: (() => void | Promise<void>) | null): void {
    this.stateSignal.update(state => ({ ...state, footerAction: action }));
  }

  setShowFooter(show: boolean): void {
     this.stateSignal.update(state => ({ ...state, showFooter: show }));
  }

  setShowFooterButton(show: boolean): void {
      this.stateSignal.update(state => ({ ...state, showFooterButton: show }));
  }

  setHeaderTemplate(template: TemplateRef<any> | null): void {
     this.stateSignal.update(state => ({ ...state, headerTemplate: template }));
  }

  setFooterTemplate(template: TemplateRef<any> | null): void {
     this.stateSignal.update(state => ({ ...state, footerTemplate: template }));
  }

  triggerClose(): void {
    this.closeSubject.next();
  }

  triggerDismiss(role: string = 'cancel', data?: any): void {
    this.dismissSubject.next({ role, data });
  }

  getCurrentState(): ControllableModalState {
    return this.state();
  }
}